import type { Priority } from "@app/core";
import type { PointerEvent as ReactPointerEvent, ReactNode } from "react";
import type { I18nKey } from "../../lib/language.js";
import { useI18n } from "../../lib/useI18n.js";
import { usePriorityFilterStore, useSettingsStore } from "../../stores/index.js";
import { SidebarSection } from "../sidebar/SidebarSection.js";
import { PRIORITY_FLAG_HEX } from "../task-colors.js";

/** Sidebar priority filter: four togglable chips inside a collapsible (persisted)
 *  section. Selected priorities filter every task-listing view (empty = no
 *  filter). A task has exactly one priority, so there's no any/all mode. */
const ORDER: Array<{ value: Priority; labelKey: I18nKey }> = [
  { value: "high", labelKey: "sidebar.priority.high" },
  { value: "medium", labelKey: "sidebar.priority.medium" },
  { value: "low", labelKey: "sidebar.priority.low" },
  { value: "none", labelKey: "sidebar.priority.none" },
];

export function PriorityFilter({ grip, onTitlePointerDown }: { grip?: ReactNode; onTitlePointerDown?: (e: ReactPointerEvent) => void } = {}) {
  const { t } = useI18n();
  const { selected, toggle, clear } = usePriorityFilterStore();
  const sectionCollapsed = useSettingsStore((s) => s.sidebarCollapsed.priority);
  const setSidebarCollapsed = useSettingsStore((s) => s.setSidebarCollapsed);

  return (
    <SidebarSection
      title={t("sidebar.priority")}
      grip={grip}
      onTitlePointerDown={onTitlePointerDown}
      collapsed={sectionCollapsed}
      onToggle={() => void setSidebarCollapsed("priority", !sectionCollapsed)}
    >
      <div className="flex flex-wrap items-center gap-1 px-2">
        {ORDER.map(({ value, labelKey }) => {
          const active = selected.includes(value);
          const hex = PRIORITY_FLAG_HEX[value];
          const label = t(labelKey);
          return (
            <button
              key={value}
              type="button"
              onClick={() => toggle(value)}
              aria-pressed={active}
              className={`flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs hover:bg-accent ${active ? "bg-accent font-medium" : "text-muted-foreground"}`}
              title={active ? t("sidebar.filteringPriority", { label: label.toLowerCase() }) : t("sidebar.filterPriority", { label: label.toLowerCase() })}
            >
              <span className="h-2 w-2 shrink-0 rounded-full border" style={{ background: hex || "transparent" }} />
              {label}
            </button>
          );
        })}
        {selected.length > 0 && (
          <button type="button" onClick={clear} className="text-xs text-muted-foreground hover:text-foreground">{t("sidebar.clear")}</button>
        )}
      </div>
    </SidebarSection>
  );
}
